import { inject, Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap, tap } from 'rxjs/operators';

import { useToaster } from '../../../shared/composables/use-toaster';
import { StoreService } from '../../../shared/services/store.service';
import { LoginActions } from '../../../shared/store/actions/login';
import { AppState } from '../../../shared/store/app-state';
import { isMainAccount } from '../../../shared/store/selectors/login';
import { AlertNotificationConfigGateway } from '../../gateways/AlertNotificationConfigGateway';
import { AlertsNotificationConfigService } from '../../services/alerts-notification-config.service';
import { AlertsMailingConfigActions } from '../actions/alerts-mailing-config';
import { AlertsNotificationConfigActions } from '../actions/alerts-notification-config';
import { getAlertsConfig } from '../selectors/alerts-notification-config';

@Injectable()
export class AlertsNotificationConfigEffects {
    private readonly translate = inject(TranslateService);
    private readonly toaster = useToaster();

    loadAlertNotificationConfig$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoginActions.SET_LOGIN_USER),
            switchMap(() => this.store.select(isMainAccount)),
            switchMap((mainAccount) =>
                mainAccount
                    ? this.alertNotificationConfigGateway.getAlertsNotificationConfig()
                    : of({ alertsConfig: [], mailConfig: null }),
            ),
            map((configuration) => AlertsNotificationConfigActions.alertsNotificationConfigLoaded(configuration)),
        ),
    );

    requestAlertConfigUpdateAfterToggling$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlertsNotificationConfigActions.alertNotificationToggled),
            switchMap((action) => combineLatest([of(action), this.store.select(getAlertsConfig)])),
            map(([{ name, isActive }, alertsConfig]) => {
                return this.alertsNotificationConfigService.updateAlertConfig(alertsConfig, name, isActive);
            }),
            map((alertsConfig) =>
                AlertsNotificationConfigActions.alertNotificationConfigUpdateRequested({ alertsConfig }),
            ),
        ),
    );

    saveAlertsNotificationConfigApi$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlertsNotificationConfigActions.alertNotificationConfigUpdateRequested),
            debounceTime(500),
            switchMap(({ alertsConfig }) =>
                this.alertNotificationConfigGateway.saveAlertsNotificationConfig({ config: alertsConfig }).pipe(
                    map(() => AlertsNotificationConfigActions.alertNotificationConfigUpdateSucceed({ alertsConfig })),
                    catchError(() =>
                        of(AlertsNotificationConfigActions.alertNotificationConfigUpdateFailed({ alertsConfig })),
                    ),
                ),
            ),
        ),
    );

    notifyUserWhenAlertConfigUpdateSucceeded$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AlertsNotificationConfigActions.alertNotificationConfigUpdateSucceed),
                tap(() => this.toaster.success({ title: this.translate.instant('ALERT_CONFIG_UPDATE_SUCCESS') })),
            ),
        { dispatch: false },
    );

    notifyUserWhenAlertConfigUpdateFailed$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    AlertsNotificationConfigActions.alertNotificationConfigUpdateFailed,
                    AlertsMailingConfigActions.apiFailed,
                ),
                tap(() =>
                    this.toaster.error({
                        title: this.translate.instant('ERROR_WHILE_UPDATING_ALERTS_CONFIG'),
                        duration: 15_000,
                    }),
                ),
            ),
        { dispatch: false },
    );

    constructor(
        private actions$: Actions,
        @Inject('AlertNotificationConfigGateway')
        private alertNotificationConfigGateway: AlertNotificationConfigGateway,
        private alertsNotificationConfigService: AlertsNotificationConfigService,
        private store: StoreService<AppState>,
    ) {}
}
